'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { useLocale } from 'next-intl'
import {
  Maximize2,
  Minimize2,
  RotateCcw,
  X as XIcon,
  ShieldCheck,
  ShieldOff,
  Lock,
} from 'lucide-react'
import { cn } from '@/utilities/cn'
import { ChatRobot, DRAW_H } from './ChatRobot'
import { ChatInputSuggestions } from './ChatInputSuggestions'
import { ChatOrderCTA, findProductSlugWithFallback } from './ChatOrderCTA'
import { isOrderIntent } from './chatIntent'
import { buildCartSnapshot } from './buildCartSnapshot'
import { useCartModalOpen, CART_MODAL_OFFSET_PX } from '@/components/Cart/useCartModalOpen'
import { useAuth } from '@/providers/Auth'

// Master switch for the inline "Add to cart" card under chat
// messages. See docs/chatbot-sprite/19 and
// docs/handovers/2026-07-26-chatbot-fixes-log.md (#18).
//
// The bot's job is discovery + navigation. The product page
// (src/app/(app)/[locale]/products/[slug]) already owns the
// full variant-selection + add-to-cart UX that the user has
// spent time building. An inline CTA in the chat duplicates
// that UX in a place that can't easily handle all the
// edge cases (variant matrix, stock state, qty inputs, coupon
// codes, manual delivery items, etc.) and creates a confusing
// "two surfaces, one cart" mental model. The Doofinder pattern
// (product search on the home page + a separate AI assistant
// that routes users to the product page) is the cleaner split
// — and the cheapest path to it is to NOT render the CTA in
// the chat at all right now.
//
// Flip to true when the product-page navigation flow is in
// place AND the team has decided the chat should drive orders
// directly. The supporting infrastructure (by-slug API,
// buildCartSnapshot, isOrderIntent, ChatOrderCTA component,
// all tests) stays intact so re-enabling is one constant flip.
const CHAT_CTA_ENABLED = false
import { useCart } from '@payloadcms/plugin-ecommerce/client/react'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  error?: boolean
}

interface ChatPanelProps {
  isOpen: boolean
  onClose: () => void
}

const SUGGESTIONS = ['Was verkauft ihr?', 'Öffnungszeiten?', 'Versandkosten?']

const WELCOME_MESSAGE =
  'Hallo! Ich bin der organic-seeds.at Chat-Assistent 🌿 Wie kann ich dir helfen? Ich beantworte Fragen zu unseren Produkten, Öffnungszeiten und Bestellungen.'

const IDLE_SAVE_MS = 5 * 60 * 1000
const SESSION_KEY = 'chat-messages'
const SESSION_ID_KEY = 'chat-session-id'
const LOCALE_KEY = 'chat-locale'
const CONSENT_KEY = 'chat-consent'
const SITE_BASE =
  process.env.NEXT_PUBLIC_SERVER_URL || 'https://organic-seeds.at'
// Mirrors RATE_LIMIT in src/app/(app)/api/chat/route.ts. Used to seed the
// header counter so it is visible from the start (before the first request
// returns the real X-RateLimit-* headers). Keep in sync with the server.
const DEFAULT_RATE_LIMIT = 60

type ConsentChoice = 'accepted' | 'declined' | 'pending'

function loadSession(): Message[] | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((m: any) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }))
    }
  } catch { /* corrupted session data */ }
  return null
}

function saveSession(messages: Message[]) {
  try {
    if (messages.length === 0) return
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages))
  } catch { /* quota exceeded or unavailable */ }
}

function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY)
    // Drop the session id too, so the next conversation gets its own row.
    sessionStorage.removeItem(SESSION_ID_KEY)
  } catch { /* ignore */ }
}

// Stable id for this conversation. All server-side log saves for one
// conversation upsert onto the same row instead of creating a new row on
// every autosave / panel close / unload beacon. Persists across navigation
// (sessionStorage) and resets when the conversation is cleared or the tab
// closes.
function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY)
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      sessionStorage.setItem(SESSION_ID_KEY, id)
    }
    return id
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

// ---- Window geometry (drag + resize) -------------------------------------

type Size = { w: number; h: number }
type Pos = { x: number; y: number }

const POS_KEY = 'chat-pos'
const SIZE_KEY = 'chat-size'
const NORMAL_SIZE: Size = { w: 380, h: 520 }
const LARGE_SIZE: Size = { w: 560, h: 680 }
const MIN_SIZE: Size = { w: 320, h: 380 }
const EDGE_MARGIN = 16 // gap from viewport edges
const BOTTOM_GAP = 96 // clearance above the chat trigger button

const clampN = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v))

// window.innerHeight does NOT shrink when the on-screen keyboard opens on
// iOS Safari — only window.visualViewport.height does. Sizing/positioning
// off window.innerHeight alone means the panel (and the input row inside
// it) can end up sitting under the keyboard. Every place that previously
// read window.innerHeight for vertical math now goes through this helper
// instead, and a visualViewport 'resize' listener (below, in the
// component) re-runs the same fit/clamp logic the window 'resize' handler
// already used, so the panel re-anchors above the keyboard as it opens.
function getViewportHeight(): number {
  if (typeof window === 'undefined') return 800
  return window.visualViewport?.height ?? window.innerHeight
}

// Largest size that fits the current viewport (keeps the panel usable on
// phones — the default 380×520 is clamped down on small screens).
function maxSize(): Size {
  if (typeof window === 'undefined') return { ...NORMAL_SIZE }
  return {
    w: Math.max(MIN_SIZE.w, window.innerWidth - EDGE_MARGIN * 2),
    h: Math.max(MIN_SIZE.h, getViewportHeight() - EDGE_MARGIN - BOTTOM_GAP),
  }
}

function fitSize(s: Size): Size {
  const mx = maxSize()
  return { w: clampN(s.w, MIN_SIZE.w, mx.w), h: clampN(s.h, MIN_SIZE.h, mx.h) }
}

// Default anchor: bottom-right, above the trigger button — the fixed starting
// position every time a fresh session opens.
function defaultPos(size: Size): Pos {
  if (typeof window === 'undefined') return { x: EDGE_MARGIN, y: EDGE_MARGIN }
  return {
    x: Math.max(EDGE_MARGIN, window.innerWidth - size.w - EDGE_MARGIN),
    y: Math.max(EDGE_MARGIN, getViewportHeight() - size.h - BOTTOM_GAP),
  }
}

function clampPos(p: Pos, size: Size): Pos {
  if (typeof window === 'undefined') return p
  return {
    x: clampN(p.x, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerWidth - size.w - EDGE_MARGIN)),
    y: clampN(p.y, EDGE_MARGIN, Math.max(EDGE_MARGIN, getViewportHeight() - size.h - EDGE_MARGIN)),
  }
}

function loadStoredSize(): Size | null {
  try {
    const raw = sessionStorage.getItem(SIZE_KEY)
    if (raw) {
      const s = JSON.parse(raw)
      if (s && typeof s.w === 'number' && typeof s.h === 'number') return s
    }
  } catch { /* ignore */ }
  return null
}

function loadStoredPos(): Pos | null {
  try {
    const raw = sessionStorage.getItem(POS_KEY)
    if (raw) {
      const p = JSON.parse(raw)
      if (p && typeof p.x === 'number' && typeof p.y === 'number') return p
    }
  } catch { /* ignore */ }
  return null
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ isOpen, onClose }) => {
  const siteLocale = useLocale() as 'de' | 'en'
  const { user: authUser } = useAuth()
  const { cart: ecommerceCart } = useCart()
  const isLoggedIn = Boolean(authUser && (authUser as any).id)
  // Slide left to make room for the cart Sheet when it opens. The
  // motion.div's x prop is independent of the user's drag position
  // (left/top inline styles), so the panel keeps wherever the user
  // put it on the Y axis and the cart width on the X axis.
  const cartModalOpen = useCartModalOpen()
  const [chatLocale, setChatLocaleState] = useState<'de' | 'en'>(() => {
    if (typeof window === 'undefined') return siteLocale
    try {
      const stored = localStorage.getItem(LOCALE_KEY)
      if (stored === 'de' || stored === 'en') return stored
    } catch { /* localStorage unavailable */ }
    return siteLocale
  })
  const setChatLocale = useCallback((next: 'de' | 'en') => {
    setChatLocaleState(next)
    try {
      localStorage.setItem(LOCALE_KEY, next)
    } catch { /* ignore */ }
  }, [])
  const locale = chatLocale
  const sessionMessages = loadSession()
  const [messages, setMessages] = useState<Message[]>(
    sessionMessages ?? [
      {
        role: 'assistant',
        content: WELCOME_MESSAGE,
        timestamp: new Date(),
      },
    ]
  )
  const [input, setInput] = useState('')
  const [caretPos, setCaretPos] = useState(0)
  const [hasMatches, setHasMatches] = useState(false)
  const [highlightedWords, setHighlightedWords] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  // Seeded with the default so the counter is visible immediately; the real
  // remaining/limit/reset values overwrite it once the first /api/chat
  // response returns the X-RateLimit-* headers.
  const [rateLimit, setRateLimit] = useState<{
    limit: number
    remaining: number
    resetAt: number
  } | null>({ limit: DEFAULT_RATE_LIMIT, remaining: DEFAULT_RATE_LIMIT, resetAt: 0 })
  const [resetCountdown, setResetCountdown] = useState<number>(0)
  const [lastUserMessage, setLastUserMessage] = useState<string | null>(null)
  const [consent, setConsentState] = useState<ConsentChoice>(() => {
    if (typeof window === 'undefined') return 'pending'
    try {
      const stored = localStorage.getItem(CONSENT_KEY)
      if (stored === 'accepted' || stored === 'declined') return stored
    } catch {
      /* localStorage unavailable */
    }
    return 'pending'
  })
  const setConsent = useCallback((next: ConsentChoice) => {
    setConsentState(next)
    try {
      if (next === 'pending') {
        localStorage.removeItem(CONSENT_KEY)
      } else {
        localStorage.setItem(CONSENT_KEY, next)
      }
    } catch {
      /* ignore */
    }
  }, [])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const idleSavedRef = useRef<boolean>(false)

  // ---- Drag + resize geometry --------------------------------------------
  const [size, setSize] = useState<Size>(() =>
    typeof window === 'undefined' ? { ...NORMAL_SIZE } : fitSize(loadStoredSize() ?? NORMAL_SIZE)
  )
  const [pos, setPos] = useState<Pos | null>(null)
  const [expanded, setExpanded] = useState(false)
  // Live viewport dimensions, kept in sync (window resize + visualViewport
  // resize, see the effect below) so the mobile full-screen sheet can track
  // both orientation changes and the on-screen keyboard opening/closing.
  const [liveViewport, setLiveViewport] = useState<Size>(() =>
    typeof window === 'undefined'
      ? { w: 390, h: 800 }
      : { w: window.innerWidth, h: getViewportHeight() }
  )
  // Below this, the floating draggable/resizable desktop-style window
  // doesn't make sense — render a bottom sheet instead (no drag, no
  // resize handle, no manual expand toggle: the whole point is it's already
  // maximized). Matches the sm: breakpoint (640px) used elsewhere in this
  // file for mobile-specific behavior.
  const isMobile = liveViewport.w < 640
  // Empty space reserved above the mobile sheet for Hans (ChatRobot.tsx) to
  // perch in, same way the floating desktop panel always had page
  // background above it for him to sit on. DRAW_H (his rendered height) is
  // exactly the amount needed for his feet to reach the reserve's bottom
  // edge (see geom()'s "- DRAW_H + 8" perch offset, which is unchanged and
  // unaware of mobile vs desktop) — env(safe-area-inset-top) on top of that
  // so his head clears the notch/status bar rather than getting clipped.
  const MOBILE_TOP_RESERVE = `calc(${DRAW_H}px + env(safe-area-inset-top))`

  const persistGeometry = useCallback((p: Pos | null, s: Size | null) => {
    try {
      if (p) sessionStorage.setItem(POS_KEY, JSON.stringify(p))
      if (s) sessionStorage.setItem(SIZE_KEY, JSON.stringify(s))
    } catch { /* ignore */ }
  }, [])

  // Resolve the starting position once the panel opens (stored → default).
  useEffect(() => {
    if (!isOpen) return
    setPos((prev) => prev ?? loadStoredPos() ?? defaultPos(size))
    // size intentionally excluded: default is computed from current size only
    // on first open, and must not jump when the user later resizes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Keep the window on-screen when the viewport changes.
  useEffect(() => {
    const onResize = () => {
      setLiveViewport({ w: window.innerWidth, h: getViewportHeight() })
      setSize((s) => fitSize(s))
      setPos((p) => (p ? clampPos(p, size) : p))
    }
    window.addEventListener('resize', onResize)
    // Also listen on visualViewport specifically: when the on-screen
    // keyboard opens on mobile, window 'resize' often doesn't fire (iOS
    // Safari) but visualViewport 'resize' always does. Re-running the same
    // fit/clamp re-anchors the panel above the keyboard instead of letting
    // it sit under it. See getViewportHeight() above for why.
    window.visualViewport?.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.visualViewport?.removeEventListener('resize', onResize)
    }
  }, [size])

  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      // No dragging the full-screen mobile sheet — there's nowhere to drag
      // it to, and it would only fight with scrolling the message list.
      if (isMobile) return
      // Don't start a drag from an interactive control in the header.
      if ((e.target as HTMLElement).closest('button, a, input, textarea, [role="group"]')) {
        return
      }
      e.preventDefault()
      const startX = e.clientX
      const startY = e.clientY
      const origin = pos ?? defaultPos(size)
      const move = (ev: PointerEvent) => {
        setPos(clampPos({ x: origin.x + (ev.clientX - startX), y: origin.y + (ev.clientY - startY) }, size))
      }
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        setPos((p) => {
          persistGeometry(p, null)
          return p
        })
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    [pos, size, persistGeometry, isMobile]
  )

  const startResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const startX = e.clientX
      const startY = e.clientY
      const origin = { ...size }
      const anchor = pos ?? defaultPos(size)
      const move = (ev: PointerEvent) => {
        const mx = maxSize()
        const nw = clampN(origin.w + (ev.clientX - startX), MIN_SIZE.w, Math.min(mx.w, window.innerWidth - anchor.x - EDGE_MARGIN))
        const nh = clampN(origin.h + (ev.clientY - startY), MIN_SIZE.h, Math.min(mx.h, getViewportHeight() - anchor.y - EDGE_MARGIN))
        setSize({ w: nw, h: nh })
        setExpanded(false)
      }
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        setSize((s) => {
          persistGeometry(null, s)
          return s
        })
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    [size, pos, persistGeometry]
  )

  const toggleExpand = useCallback(() => {
    setExpanded((wasExpanded) => {
      const next = fitSize(wasExpanded ? NORMAL_SIZE : LARGE_SIZE)
      setSize(next)
      setPos((prev) => {
        const np = clampPos(prev ?? defaultPos(next), next)
        persistGeometry(np, next)
        return np
      })
      return !wasExpanded
    })
  }, [persistGeometry])

  const activePos = pos ?? defaultPos(size)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading, scrollToBottom])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 96)}px`
  }, [input])

  // Read rate-limit state from response headers
  const readRateLimitHeaders = useCallback((response: Response) => {
    const limit = response.headers.get('X-RateLimit-Limit')
    const remaining = response.headers.get('X-RateLimit-Remaining')
    const resetAtSec = response.headers.get('X-RateLimit-Reset')
    if (limit && remaining != null && resetAtSec) {
      setRateLimit({
        limit: parseInt(limit, 10),
        remaining: parseInt(remaining, 10),
        resetAt: parseInt(resetAtSec, 10) * 1000,
      })
    }
  }, [])

  // Countdown to rate-limit reset
  useEffect(() => {
    if (!rateLimit || rateLimit.remaining > 0) {
      setResetCountdown(0)
      return
    }
    const tick = () => {
      const ms = rateLimit.resetAt - Date.now()
      setResetCountdown(Math.max(0, Math.ceil(ms / 1000)))
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [rateLimit])

  // Focus textarea when panel opens; close on Escape
  useEffect(() => {
    if (!isOpen) return
    const t = setTimeout(() => textareaRef.current?.focus(), 50)
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => {
      clearTimeout(t)
      window.removeEventListener('keydown', handleKey)
    }
  }, [isOpen, onClose])

  // Persist messages to sessionStorage so they survive navigation
  useEffect(() => {
    saveSession(messages)
  }, [messages])

  const saveChatLog = useCallback(async () => {
    // GDPR: only persist to server-side log if the user explicitly
    // accepted. The chat itself still works without consent — the LLM
    // call is a necessary part of the service. Only the local QA log
    // is gated. The server route also rejects writes without
    // consent=true as a defence-in-depth check.
    if (consent !== 'accepted') return
    if (messages.length === 0) return
    try {
      await fetch('/api/chat/save-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, consent: true, sessionId: getSessionId() }),
      })
      idleSavedRef.current = true
    } catch {
      // silent — log save is best-effort
    }
  }, [messages, consent])

  useEffect(() => {
    if (!isOpen) return
    lastActivityRef.current = Date.now()
    idleSavedRef.current = false

    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current
      // Fire once per idle period. Without the idleSavedRef guard this saved
      // every 30s for as long as the panel stayed idle. saveChatLog() sets
      // the ref; bumpActivity() resets it on the next user action.
      if (idle >= IDLE_SAVE_MS && !idleSavedRef.current) {
        saveChatLog()
      }
    }, 30_000)

    return () => clearInterval(interval)
  }, [isOpen, saveChatLog])

  // Personalise the welcome bubble once we know the user is logged in.
  // Only touches the default welcome (sessionStorage restores win), and
  // only when the customer has not started a real conversation yet (so
  // we don't rewrite a message they already saw / sent). The bot's
  // first response will then continue with the same greeting tone (the
  // system prompt also instructs it to greet by first name once).
  useEffect(() => {
    if (!isLoggedIn) return
    if (!authUser) return
    const firstName = String((authUser as any)?.name || '').split(/\s+/)[0]
    if (!firstName) return
    setMessages((prev) => {
      // Don't touch restored sessions or any conversation past the welcome.
      if (prev.length !== 1) return prev
      if (prev[0].role !== 'assistant') return prev
      if (!prev[0].content.startsWith('Hallo! Ich bin der organic-seeds.at')) return prev
      const personalised =
        locale === 'en'
          ? `Hello ${firstName}! I'm the organic-seeds.at chat assistant 🌿 How can I help you? I can answer questions about our products, opening hours, and your orders.`
          : `Hallo ${firstName}! Ich bin der organic-seeds.at Chat-Assistent 🌿 Wie kann ich dir helfen? Ich beantworte Fragen zu unseren Produkten, Öffnungszeiten und deinen Bestellungen.`
      if (prev[0].content === personalised) return prev
      return [{ ...prev[0], content: personalised, timestamp: new Date() }]
    })
  }, [isLoggedIn, authUser, locale])

  // Incremental save: persist the conversation as it happens, not only on
  // close / idle / unload. Fires a debounced upsert after each settled change
  // (e.g. once an assistant reply lands and isLoading drops). Because the
  // server upserts on sessionId, every fire updates the SAME row — so the log
  // stays current for active conversations and there is exactly one row per
  // session. Debounced to coalesce the user+assistant message pair into a
  // single write.
  useEffect(() => {
    if (consent !== 'accepted') return
    if (messages.length <= 1) return
    if (isLoading) return
    const t = setTimeout(() => {
      saveChatLog()
    }, 1500)
    return () => clearTimeout(t)
  }, [messages, consent, isLoading, saveChatLog])

  // On panel close: save to server log if there was a real conversation
  useEffect(() => {
    if (isOpen) return
    if (messages.length > 1) {
      saveChatLog()
    } else {
      clearSession()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // On page unload / tab close: persist to sessionStorage + server.
  // Only fire the beacon if consent was given — otherwise the server
  // rejects the write with 400, which clutters the dev logs.
  useEffect(() => {
    const handleUnload = () => {
      saveSession(messages)
      if (
        messages.length > 1 &&
        consent === 'accepted' &&
        navigator.sendBeacon
      ) {
        const blob = new Blob(
          [JSON.stringify({ messages, consent: true, sessionId: getSessionId() })],
          { type: 'application/json' },
        )
        navigator.sendBeacon('/api/chat/save-log', blob)
      }
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [messages, consent])

  const bumpActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
    idleSavedRef.current = false
  }, [])

  // The cart snapshot is computed by ./buildCartSnapshot — see the
  // long comment there for the units story (plugin gives euros,
  // helper converts to cents so the chat route's formatPriceCents is
  // correct). All we do here is gate it on the logged-in state and
  // memo it.
  const buildCartSnapshotMemo = useCallback(
    () => (isLoggedIn ? buildCartSnapshot(ecommerceCart) : null),
    [isLoggedIn, ecommerceCart],
  )

  const sendApiRequest = useCallback(
    async (msgs: Message[]) => {
      setIsLoading(true)
      bumpActivity()
      try {
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // The server pulls the user from the auth cookie (more
          // authoritative than any client claim), so we only need to
          // forward the cart snapshot here.
          body: JSON.stringify({
            messages: msgs.map((m) => ({ role: m.role, content: m.content })),
            locale,
            cart: buildCartSnapshotMemo(),
          }),
        })

        readRateLimitHeaders(response)

        if (response.status === 429) {
          let serverText = 'Du hast zu viele Anfragen gesendet. Bitte warte einen Moment und versuche es erneut.'
          let resetAt: number | null = null
          try {
            const errData = await response.json()
            if (errData?.error) serverText = errData.error
            if (typeof errData?.resetAt === 'number') resetAt = errData.resetAt
          } catch {
            /* fall back to default text */
          }
          if (resetAt) {
            const secs = Math.max(1, Math.ceil((resetAt - Date.now()) / 1000))
            serverText = `${serverText} (Reset in ${secs}s)`
          }
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant' as const,
              content: serverText,
              timestamp: new Date(),
              error: true,
            },
          ])
          return
        }

        if (response.status === 504 || response.status === 503) {
          let serverText = 'Der Assistent antwortet gerade nicht, bitte später erneut.'
          try {
            const errData = await response.json()
            if (errData?.error) serverText = errData.error
          } catch {
            /* fall back to default text */
          }
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant' as const,
              content: serverText,
              timestamp: new Date(),
              error: true,
            },
          ])
          return
        }

        if (response.status === 502) {
          let serverText =
            'Der Assistent konnte keine Antwort generieren. Bitte formuliere deine Frage anders oder starte eine neue Unterhaltung.'
          let isRetryable = true
          try {
            const errData = await response.json()
            if (errData?.message) serverText = errData.message
            if (errData?.error === 'EMPTY_RESPONSE') isRetryable = true
          } catch {
            /* fall back to default text */
          }
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant' as const,
              content: serverText,
              timestamp: new Date(),
              error: true,
              ...(isRetryable ? {} : {}),
            },
          ])
          return
        }

        if (!response.ok) throw new Error('Chat request failed')

        const data = await response.json()
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant' as const,
            content: data.message,
            timestamp: new Date(),
          },
        ])
      } catch (error) {
        console.error('Chat error:', error)
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant' as const,
            content:
              'Entschuldigung, es gab einen Fehler. Bitte versuche es später erneut.',
            timestamp: new Date(),
            error: true,
          },
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [bumpActivity, locale, readRateLimitHeaders, buildCartSnapshotMemo]
  )

  const sendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || isLoading) return
      bumpActivity()
      const trimmed = content.trim()
      setLastUserMessage(trimmed)
      const userMessage: Message = {
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      }
      const newMessages = [...messages, userMessage]
      setMessages(newMessages)
      setInput('')
      sendApiRequest(newMessages)
    },
    [messages, isLoading, sendApiRequest, bumpActivity]
  )

  const retryLastMessage = useCallback(() => {
    if (lastUserMessage && !isLoading) {
      sendMessage(lastUserMessage)
    }
  }, [lastUserMessage, isLoading, sendMessage])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(input)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const restartConversation = useCallback(() => {
    setMessages([
      {
        role: 'assistant',
        content: WELCOME_MESSAGE,
        timestamp: new Date(),
      },
    ])
    clearSession()
  }, [])

  const isWelcome =
    messages.length === 1 && messages[0].role === 'assistant'

  // Drives Hans's expression: busy while generating, error on a failed reply,
  // waiting while awaiting user approval/first prompt, otherwise idle.
  const lastMessage = messages[messages.length - 1]
  const hansActivity: 'idle' | 'busy' | 'waiting' | 'error' = isLoading
    ? 'busy'
    : lastMessage?.role === 'assistant' && lastMessage?.error
      ? 'error'
      : consent === 'pending' || isWelcome
        ? 'waiting'
        : 'idle'

  return (
    <>
      <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          // The CART_MODAL_OFFSET_PX (488px) left-shift is sized for the
          // cart Sheet's desktop max-width (448px). On mobile the Sheet is
          // 75% of the viewport instead, and shifting a full-screen chat
          // sheet left by 488px would push it almost entirely off-screen —
          // so only apply the shift when there's a floating desktop panel
          // to shift in the first place.
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            x: !isMobile && cartModalOpen ? -CART_MODAL_OFFSET_PX : 0,
          }}
          exit={{ opacity: 0, y: 16, scale: 0.96 }}
          transition={{
            opacity: { duration: 0.2, ease: 'easeOut' },
            scale: { duration: 0.2, ease: 'easeOut' },
            // Match the Sheet slide (500ms in / 300ms out) — one duration
            // is the visual midpoint so the panel and the cart edge
            // stay in lock-step on the way out AND in.
            x: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
            y: { duration: 0.2, ease: 'easeOut' },
          }}
          // z-41: just above the chat trigger (z-40) and well below the
          // Radix Sheet / Dialog / Search stack at z-50. If you raise
          // this, the chat panel renders on top of the cart modal when
          // the cart is open (the bug fixed in docs/chatbot-sprite/19).
          // EXCEPTION: when the cart is open we also raise to z-51 so
          // the customer can keep using the chat (the panel slides
          // left of the cart, no overlap in x, so being above the
          // cart's z-50 overlay only affects the chat's own area). On
          // mobile the sheet's top sits below MOBILE_TOP_RESERVE — clear
          // of the sticky site header — so no special-case is needed here
          // the way there is for Hans (ChatRobot.tsx), who perches inside
          // that reserved gap and does need to clear the header's z-50.
          className={`fixed ${cartModalOpen ? 'z-[51]' : 'z-[41]'} flex flex-col overflow-hidden bg-white/90 text-neutral-800 shadow-[0_8px_32px_rgba(0,0,0,0.18)] backdrop-blur-2xl dark:bg-neutral-900/90 dark:text-white dark:shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)] ${
            // Thicker + more visible top border on the mobile sheet than the
            // desktop panel's plain 1px border — this edge is where Hans's
            // feet land (see ChatRobot.tsx geom()'s "- DRAW_H + 8" perch
            // offset), so it needs to read as an actual surface/ledge he's
            // standing on, not just a hairline.
            isMobile
              ? 'inset-x-0 bottom-0 rounded-t-2xl border-0 border-t-2 border-black/20 dark:border-white/20'
              : 'rounded-2xl border border-black/10 dark:border-white/10'
          }`}
          style={
            isMobile
              ? {
                  fontFamily: 'var(--font-sans)',
                  left: 0,
                  top: MOBILE_TOP_RESERVE,
                  width: liveViewport.w,
                  height: `calc(${liveViewport.h}px - ${MOBILE_TOP_RESERVE})`,
                }
              : {
                  fontFamily: 'var(--font-sans)',
                  left: activePos.x,
                  top: activePos.y,
                  width: size.w,
                  height: size.h,
                }
          }
        >
          {/* Header — also the drag handle. Single row: brand on the left,
              the DE/EN toggle centered, window controls on the right. */}
          <div
            onPointerDown={startDrag}
            // Grid, not flex: with a plain flex row + mx-auto on the toggle,
            // "centered" only means centered in the LEFTOVER space after the
            // brand (left) and window-controls (right) groups — since those
            // two groups aren't the same width, the toggle visibly drifted
            // off true-center. grid-cols-[1fr_auto_1fr] gives both side
            // groups equal-width tracks, so the middle (auto) track is
            // always the true horizontal center regardless of how wide the
            // title text or icon group are.
            // pt-3.5/pb-2.5 (14px/10px) instead of even py-2.5: symmetric
            // padding read as too close to the top border/edge — a bit more
            // room above than below settles the row visually.
            className="grid grid-cols-[1fr_auto_1fr] shrink-0 cursor-grab touch-none select-none items-center gap-2 border-b border-black/10 bg-black/[0.03] px-3.5 pt-3.5 pb-2.5 active:cursor-grabbing dark:border-white/10 dark:bg-white/5"
          >
            {/* Brand */}
            <div className="flex min-w-0 items-center gap-2">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--neon-orange)] shadow-[0_0_8px_var(--neon-orange)]"
              />
              <h3 className="m-0 truncate text-xs font-semibold uppercase tracking-wider text-neutral-800 dark:text-white/90">
                Chat with Hans
              </h3>
            </div>

            {/* Language toggle — true-centered via the grid's auto column,
                not mx-auto (see className comment above) */}
            <div
              className="flex h-9 shrink-0 items-center overflow-hidden rounded-full border border-black/10 bg-black/[0.04] font-mono text-micro font-semibold uppercase tracking-wider dark:border-white/10 dark:bg-white/5"
              role="group"
              aria-label="Chat-Sprache"
            >
              <button
                type="button"
                onClick={() => setChatLocale('de')}
                aria-pressed={locale === 'de'}
                className={`flex h-full cursor-pointer items-center px-3.5 transition-colors ${
                  locale === 'de'
                    ? 'bg-[var(--neon-orange)] text-black'
                    : 'text-neutral-500 hover:bg-black/5 hover:text-neutral-800 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white'
                }`}
                title="Auf Deutsch antworten"
              >
                DE
              </button>
              <button
                type="button"
                onClick={() => setChatLocale('en')}
                aria-pressed={locale === 'en'}
                className={`flex h-full cursor-pointer items-center px-3.5 transition-colors ${
                  locale === 'en'
                    ? 'bg-[var(--neon-orange)] text-black'
                    : 'text-neutral-500 hover:bg-black/5 hover:text-neutral-800 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white'
                }`}
                title="Reply in English"
              >
                EN
              </button>
            </div>

            {/* Window controls — justify-self-end hugs the right edge of its
                grid column; gap-2 matches the brand group's gap so both
                sides follow the same spacing rhythm (was gap-0.5 = 2px,
                visibly tighter than the brand group's 8px). */}
            <div className="flex shrink-0 items-center justify-self-end gap-2">
              {rateLimit && (
                <span
                  className={`${rateLimit.remaining <= 3 ? 'flex' : 'hidden sm:flex'} h-6 items-center gap-0.5 rounded-full px-2 font-mono text-micro font-semibold tabular-nums transition-colors ${
                    rateLimit.remaining === 0
                      ? 'bg-[var(--neon-orange)]/15 text-neon-orange dark:bg-[var(--neon-orange)]/20'
                      : rateLimit.remaining <= 3
                        ? 'bg-yellow-500/20 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300'
                        : 'bg-black/[0.06] text-neutral-600 dark:bg-white/10 dark:text-white/70'
                  }`}
                  title={
                    rateLimit.remaining === 0
                      ? `Rate-Limit erreicht — Reset in ${resetCountdown}s`
                      : `${rateLimit.remaining} von ${rateLimit.limit} Anfragen verbleibend`
                  }
                  aria-label={
                    rateLimit.remaining === 0
                      ? `Rate-Limit erreicht, Reset in ${resetCountdown} Sekunden`
                      : `${rateLimit.remaining} Anfragen verbleibend`
                  }
                >
                  {rateLimit.remaining === 0 ? (
                    <>
                      <RotateCcw size={11} strokeWidth={2.5} />
                      {resetCountdown}s
                    </>
                  ) : (
                    <>
                      {rateLimit.remaining}
                      <span className="opacity-50">/</span>
                      <span className="opacity-50">{rateLimit.limit}</span>
                    </>
                  )}
                </span>
              )}
              {consent !== 'pending' && (
                <button
                  type="button"
                  onClick={() => setConsent('pending')}
                  className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none bg-transparent transition-colors hover:bg-black/5 dark:hover:bg-white/10 ${
                    consent === 'accepted'
                      ? 'text-green-600 hover:text-green-700 dark:text-green-400/80 dark:hover:text-green-300'
                      : 'text-neutral-400 hover:text-neutral-600 dark:text-white/40 dark:hover:text-white/70'
                  }`}
                  title={
                    consent === 'accepted'
                      ? locale === 'en'
                        ? 'Saving this chat for quality assurance is ON — click to change'
                        : 'Speicherung dieses Chats zur Qualitätssicherung ist AN — klicken zum Ändern'
                      : locale === 'en'
                        ? 'Chat saving is OFF — click to change'
                        : 'Chat-Speicherung ist AUS — klicken zum Ändern'
                  }
                  aria-label={
                    consent === 'accepted'
                      ? locale === 'en' ? 'Chat saving on' : 'Chat-Speicherung an'
                      : locale === 'en' ? 'Chat saving off' : 'Chat-Speicherung aus'
                  }
                >
                  {consent === 'accepted' ? <ShieldCheck size={16} /> : <ShieldOff size={16} />}
                </button>
              )}
              {/* Manual expand/shrink is a floating-window concept — on
                  mobile the sheet is already full-screen, so there's
                  nothing for this button to do. */}
              {!isMobile && (
                <button
                  type="button"
                  onClick={toggleExpand}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-neutral-500 transition-colors hover:bg-black/5 hover:text-neutral-900 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
                  aria-label={
                    expanded
                      ? locale === 'en' ? 'Shrink window' : 'Fenster verkleinern'
                      : locale === 'en' ? 'Enlarge window' : 'Fenster vergrößern'
                  }
                  title={
                    expanded
                      ? locale === 'en' ? 'Shrink window' : 'Fenster verkleinern'
                      : locale === 'en' ? 'Enlarge window' : 'Fenster vergrößern'
                  }
                >
                  {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              )}
              <button
                type="button"
                onClick={restartConversation}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-neutral-500 transition-colors hover:bg-black/5 hover:text-neutral-900 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
                aria-label="Unterhaltung neu starten"
                title={locale === 'en' ? 'Restart conversation' : 'Unterhaltung neu starten'}
              >
                <RotateCcw size={16} />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-red-500 transition-colors hover:bg-red-500/10 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-400/10 dark:hover:text-red-300"
                aria-label={locale === 'en' ? 'Close chat' : 'Chat schließen'}
                title={locale === 'en' ? 'Close' : 'Schließen'}
              >
                <XIcon size={17} />
              </button>
            </div>
          </div>

          {/* Body: scrollable messages + consent popup overlay */}
          <div className="relative flex min-h-0 flex-1 flex-col">
            {/* Messages */}
            <div
              className="flex flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-4 py-3"
              role="log"
              aria-live="polite"
            >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex max-w-[85%] flex-col ${
                  msg.role === 'user'
                    ? 'self-end items-end'
                    : 'self-start items-start'
                }`}
              >
                <div
                  className={`break-words px-3.5 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'rounded-2xl rounded-br-md font-sans text-black'
                      : 'rounded-2xl rounded-bl-md bg-black/[0.06] font-sans text-neutral-800 dark:bg-white/10 dark:text-white/90'
                  }`}
                  style={
                    msg.role === 'user'
                      ? { background: 'var(--neon-orange, hsl(24 100% 50%))' }
                      : undefined
                  }
                >
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <p className="m-0 [&:not(:last-child)]:mb-2">
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul className="my-1 list-disc pl-4">{children}</ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="my-1 list-decimal pl-4">{children}</ol>
                        ),
                        li: ({ children }) => (
                          <li className="my-0.5">{children}</li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-neutral-900 dark:text-white">
                            {children}
                          </strong>
                        ),
                        em: ({ children }) => (
                          <em className="text-neutral-600 dark:text-white/80">{children}</em>
                        ),
                        code: ({
                          className,
                          children,
                        }: {
                          className?: string
                          children?: React.ReactNode
                        }) => {
                          const isInline = !className
                          return isInline ? (
                            <code className="rounded bg-black/[0.06] px-1 py-0.5 font-mono text-xs text-neutral-700 dark:bg-white/10 dark:text-white/80">
                              {children}
                            </code>
                          ) : (
                            <code className="my-1 block overflow-x-auto rounded bg-black/[0.06] px-2 py-1 font-mono text-xs text-neutral-700 dark:bg-white/10 dark:text-white/80">
                              {children}
                            </code>
                          )
                        },
                        pre: ({ children }) => (
                          <pre className="my-1 overflow-x-auto rounded-lg bg-black/[0.04] p-2 text-xs text-neutral-700 dark:bg-white/5 dark:text-white/80">
                            {children}
                          </pre>
                        ),
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium underline underline-offset-2"
                            style={{
                              color: 'var(--neon-orange, hsl(24 100% 50%))',
                            }}
                          >
                            {children}
                          </a>
                        ),
                        h1: ({ children }) => (
                          <h1 className="my-2 text-base font-bold text-neutral-900 dark:text-white">
                            {children}
                          </h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="my-1.5 text-sm font-bold text-neutral-900 dark:text-white">
                            {children}
                          </h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="my-1 text-sm font-semibold text-neutral-800 dark:text-white/90">
                            {children}
                          </h3>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote
                            className="my-1 border-l-2 pl-3 italic text-neutral-600 dark:text-white/70"
                            style={{
                              borderColor:
                                'var(--neon-orange, hsl(24 100% 50%))',
                            }}
                          >
                            {children}
                          </blockquote>
                        ),
                        hr: () => <hr className="my-2 border-black/10 dark:border-white/10" />,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    <span className="font-medium">{msg.content}</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2 px-1">
                  <span className="font-mono text-micro uppercase tracking-wider text-neutral-400 dark:text-white/30">
                    {msg.timestamp.toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {msg.error && idx === messages.length - 1 && lastUserMessage && !isLoading && (
                    <button
                      type="button"
                      onClick={retryLastMessage}
                      className="flex h-5 cursor-pointer items-center gap-1 rounded-full border-none bg-black/[0.06] px-2 font-mono text-micro uppercase tracking-wider text-neutral-600 transition-colors hover:bg-black/10 hover:text-neutral-900 dark:bg-white/10 dark:text-white/70 dark:hover:bg-white/20 dark:hover:text-white"
                      aria-label="Letzte Nachricht erneut senden"
                    >
                      ↻ Erneut versuchen
                    </button>
                  )}
                </div>
                {/* Inline "place this order" CTA — only for logged-in
                    customers, only on assistant messages that mention
                    a product link AND the immediately-preceding user
                    message shows real order intent (a quantity, a buy
                    verb, "I want", "bestellen", etc.). This is the
                    fix for the "Stecklinge" browse-query case where
                    the bot lists 13 products and we don't want a CTA
                    under every single one.

                    CURRENTLY DISABLED — see CHAT_CTA_ENABLED above
                    and fixes-log #18. The bot now just deep-links
                    to the product page; the existing product-page
                    UX owns the variant + add-to-cart flow. */}
                {CHAT_CTA_ENABLED && isLoggedIn && msg.role === 'assistant' && !msg.error && (() => {
                  // Two-step slug lookup. The current message is the
                  // primary source (the bot just linked to a product).
                  // FALLBACK: if the current message has no link (e.g.
                  // a clarification reply — "which variant would
                  // you like?"), look back through the conversation
                  // for the most recent product link the bot mentioned.
                  // This keeps the CTA alive in the "I want 3x X →
                  // bot asks 'which variant?'" flow (bug 2026-07-26
                  // with Borneo Kratom) — the user still gets a
                  // clickable card for the variant the bot last
                  // surfaced. See findProductSlugWithFallback in
                  // ChatOrderCTA.tsx.
                  const slug = findProductSlugWithFallback(messages, idx)
                  if (!slug) return null
                  // Find the most recent user message BEFORE this
                  // assistant message. If the customer is just
                  // browsing ("Stecklinge", "what do you have") we
                  // skip the CTA; if they want to order ("5x", "I
                  // want", "bestellen") we show it.
                  let prevUser: Message | null = null
                  for (let j = idx - 1; j >= 0; j--) {
                    if (messages[j].role === 'user') {
                      prevUser = messages[j]
                      break
                    }
                  }
                  if (!prevUser || !isOrderIntent(prevUser.content)) return null
                  return <ChatOrderCTA productSlug={slug} />
                })()}
              </div>
            ))}

            {/* Loading indicator — animated bouncing dots */}
            {isLoading && (
              <div className="flex max-w-[85%] flex-col self-start">
                <div className="rounded-2xl rounded-bl-md bg-black/[0.06] px-3.5 py-2.5 dark:bg-white/10">
                  <span className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-white/50">
                    organic-seeds.at antwortet
                    <span className="inline-flex items-end gap-0.5 pb-0.5">
                      <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 dark:bg-white/40" />
                      <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 animation-delay-200 dark:bg-white/40" />
                      <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 animation-delay-500 dark:bg-white/40" />
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* Suggestion chips */}
            {isWelcome && !isLoading && (
              <div className="mt-1 flex flex-wrap gap-2">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => sendMessage(suggestion)}
                    className="cursor-pointer rounded-full border border-black/10 bg-black/[0.03] px-3 py-1.5 font-mono text-xs text-neutral-600 transition-all hover:border-black/20 hover:bg-black/[0.06] hover:text-neutral-900 dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/30 dark:hover:bg-white/10 dark:hover:text-white"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
            </div>

            {/* Consent popup (GDPR) — light, formal card over the conversation
                until the user chooses. The chat works without consent; only the
                QA log is gated. */}
            {consent === 'pending' && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]"
                role="dialog"
                aria-modal="true"
                aria-label={locale === 'en' ? 'Privacy notice' : 'Datenschutzhinweis'}
              >
                <div className="w-full max-w-[300px] rounded-2xl border border-black/10 bg-white/95 p-4 text-neutral-700 shadow-[0_16px_48px_rgba(0,0,0,0.5)]">
                  <div className="mb-2.5 flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--neon-orange)]/15 text-neon-orange">
                      <Lock size={13} strokeWidth={2.5} />
                    </span>
                    <h4 className="m-0 text-micro font-semibold uppercase tracking-wider text-neutral-800">
                      {locale === 'en' ? 'Privacy notice' : 'Datenschutzhinweis'}
                    </h4>
                  </div>
                  <p className="m-0 mb-3.5 text-caption leading-relaxed">
                    {locale === 'en' ? (
                      <>
                        This conversation is processed by our chat AI (DeepSeek
                        via OpenCode). For quality assurance we store it on our
                        servers for 90 days. Data is not shared with third
                        parties. See our{' '}
                        <a
                          href={`${SITE_BASE}/datenschutz`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-neon-orange underline underline-offset-2"
                        >
                          privacy policy
                        </a>
                        .
                      </>
                    ) : (
                      <>
                        Diese Unterhaltung wird von unserem Chat-Assistenten
                        (DeepSeek über OpenCode) verarbeitet. Zur
                        Qualitätssicherung speichern wir sie 90 Tage auf unseren
                        Servern. Die Daten werden nicht an Dritte weitergegeben.
                        Mehr in unserer{' '}
                        <a
                          href={`${SITE_BASE}/datenschutz`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-neon-orange underline underline-offset-2"
                        >
                          Datenschutzerklärung
                        </a>
                        .
                      </>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConsent('accepted')}
                      className="flex-1 cursor-pointer rounded-full border-none bg-[var(--neon-orange)] px-3 py-2 font-mono text-micro font-semibold uppercase tracking-wider text-black transition-opacity hover:opacity-90"
                    >
                      {locale === 'en' ? 'Accept' : 'Akzeptieren'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConsent('declined')}
                      className="flex-1 cursor-pointer rounded-full border border-neutral-300 bg-transparent px-3 py-2 font-mono text-micro uppercase tracking-wider text-neutral-600 transition-colors hover:bg-neutral-100"
                    >
                      {locale === 'en' ? 'Decline' : 'Ablehnen'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input area */}
          <div
            className="flex shrink-0 flex-col border-t border-black/10 bg-black/[0.03] p-3 dark:border-white/10 dark:bg-white/5"
            style={isMobile ? { paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' } : undefined}
          >
            {!isLoading && (
              <ChatInputSuggestions
                value={input}
                caret={caretPos}
                onHasMatchesChange={setHasMatches}
                onPick={(newText, pickedWord) => {
                  setInput(newText)
                  if (pickedWord && !highlightedWords.includes(pickedWord)) {
                    setHighlightedWords((prev) => [...prev, pickedWord])
                  }
                  setHasMatches(false)
                  if (textareaRef.current) {
                    textareaRef.current.focus()
                    const newPos = newText.length
                    textareaRef.current.setSelectionRange(newPos, newPos)
                    setCaretPos(newPos)
                  }
                }}
              />
            )}
            <form onSubmit={handleSubmit} className="flex items-end gap-2">
              <div className="relative flex-1 rounded-xl border border-black/10 bg-white/60 focus-within:border-[var(--neon-orange)]/50 dark:border-white/10 dark:bg-white/5 dark:focus-within:border-white/30 overflow-hidden transition-all duration-200">
                {/* Backdrop for highlighted words */}
                <div
                  ref={backdropRef}
                  aria-hidden="true"
                  className="absolute inset-0 px-3.5 py-2.5 text-base whitespace-pre-wrap break-words pointer-events-none text-transparent overflow-y-auto"
                  style={{ fontFamily: 'var(--font-sans)' }}
                >
                  {(() => {
                    if (!highlightedWords.length || !input) return input
                    const escaped = highlightedWords.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
                    const regex = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi')
                    const parts = input.split(regex)
                    return parts.map((part, i) => {
                      if (highlightedWords.some((w) => w.toLowerCase() === part.toLowerCase())) {
                        return (
                          <span
                            key={i}
                            className="rounded-[4px] ring-1 ring-sky-400/80 bg-sky-400/20 shadow-[0_0_8px_rgba(56,189,248,0.4)] dark:ring-orange-400/80 dark:bg-orange-400/20 dark:shadow-[0_0_8px_rgba(251,146,60,0.4)] text-transparent transition-colors duration-200"
                          >
                            {part}
                          </span>
                        )
                      }
                      return <span key={i}>{part}</span>
                    })
                  })()}
                </div>
                {/* Actual transparent textarea */}
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value)
                    setCaretPos(e.target.selectionStart)
                  }}
                  onScroll={(e) => {
                    if (backdropRef.current) {
                      backdropRef.current.scrollTop = e.currentTarget.scrollTop
                      backdropRef.current.scrollLeft = e.currentTarget.scrollLeft
                    }
                  }}
                  onKeyUp={(e) => setCaretPos((e.target as HTMLTextAreaElement).selectionStart)}
                  onClick={(e) => setCaretPos((e.target as HTMLTextAreaElement).selectionStart)}
                  onKeyDown={handleKeyDown}
                  placeholder="Schreib eine Nachricht..."
                  disabled={isLoading}
                  rows={1}
                  maxLength={2000}
                  className="relative block w-full max-h-24 resize-none bg-transparent px-3.5 py-2.5 text-base text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-white dark:placeholder:text-white/30 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ fontFamily: 'var(--font-sans)' }}
                  aria-label="Nachricht eingeben"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="shrink-0 cursor-pointer rounded-full px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-wider text-black transition-all hover:shadow-[0_0_25px_var(--neon-orange),0_0_50px_var(--neon-orange)/40] hover:scale-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 disabled:hover:shadow-none"
                style={{ background: 'var(--neon-orange, hsl(24 100% 50%))' }}
              >
                Senden
              </button>
            </form>
          </div>

          {/* Resize handle (bottom-right corner). Hidden below 640px: on
              mobile it sits right on top of the Senden button's tap zone
              (measured ~8×8px overlap at a 390px viewport) and manual
              resize isn't a useful affordance on a touch screen anyway. */}
          <div
            onPointerDown={startResize}
            title={locale === 'en' ? 'Drag to resize' : 'Zum Vergrößern ziehen'}
            className="absolute bottom-0 right-0 z-10 hidden h-5 w-5 cursor-nwse-resize touch-none items-end justify-end p-1 text-neutral-400 transition-colors hover:text-neutral-600 sm:flex dark:text-white/30 dark:hover:text-white/70"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path
                d="M9 1L1 9M9 5L5 9"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Interactive robot that perches on top of the chat window, wanders,
          and can be grabbed + dragged. Tracks the panel via panelRef. */}
      {isOpen && (
        <ChatRobot panelRef={panelRef} isMobile={isMobile} activity={hansActivity} />
      )}
    </>
  )
}
